import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient, toastOption } from "./config";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";
import Spinner from "./components/UI/Spinner";
import { QRRedirectHandler } from "./sections/FacilityQRCodeGenerator";
import Layout from "./pages/Layout";
import { SpecializationURLRedirectHandler } from "./components/SpecializationURLRedirect";
import VerifyOTP from "./pages/VerifyOTP";
import { ProtectedRoute } from "./utils/ProtectedRoute";
import { RouteHandler } from "./utils/RouteHandler";
import "./styles.css";
import { Toaster } from "react-hot-toast";
import useAuthInitalizer from "./hooks/useAuthInitalizer";
import ContactButton from "./components/UI/ContactButton";
import { isEmbedded, usePostHeightToParent } from "./utils/embed";
import { useSessionExpiry } from "./utils/session";
// import PreventInspection from "./utils/preventInspection";  // re-enable with the mount in the tree below
import SetPasswordPage from "./pages/SetPasswordPage";

const Home = lazy(() => import("./pages/Home"));
const Specializations = lazy(() => import("./pages/Specializations"));
const Doctors = lazy(() => import("./pages/Doctors"));
const TimeSlot = lazy(() => import("./pages/avaibleSlot"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutUs = lazy(() => import("./pages/abousUs"));
const FindDoctor = lazy(() => import("./pages/FindDoctor"));
const AppointmentBooking = lazy(() => import("./sections/BookAppointment"));
const FAQ = lazy(() => import("./pages/Faq"));
const CombinedQRGenerate = lazy(() => import("./pages/CombinedQRGenerate"));
const QRCodeDoctors = lazy(() => import("./pages/QRCodeDoctors"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Profile = lazy(() => import("./pages/Profile"));
const Facilities = lazy(() => import("./pages/Facilities"));
const UserChecker = lazy(() => import("./pages/GetUserByPhone"));
const FacilityEntry = lazy(() => import("./pages/FacilityEntry"));
const ForgotPasswordOtp = lazy(() => import("./pages/ForgotPasswordOtp"));
const HowToBook = lazy(() => import("./pages/HowToBook"));
function RouteTransition() {
  const location = useLocation();
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    setIsChanging(true);
    const timer = setTimeout(() => setIsChanging(false), 800);
    return () => clearTimeout(timer);
  }, [location]);

  if (!isChanging) return null;
  return <div className="route-transition" />;
}
function App() {
  useEffect(() => {
    const isInitialized = localStorage.getItem("appInitialized");
    if (!isInitialized) {
      localStorage.setItem("appInitialized", "initializing");
    }
  }, []);
  useAuthInitalizer();
  const embedded = isEmbedded();
  usePostHeightToParent(embedded);
  useSessionExpiry();
  return (
    <QueryClientProvider client={queryClient}>
      {/* Temporarily disabled so production can be inspected/debugged.
          Re-enable by uncommenting the line below. */}
      {/* {import.meta.env.PROD && <PreventInspection />} */}

      <BrowserRouter>
        <RouteHandler>
          <RouteTransition />
          <Suspense fallback={<Spinner />}>
            <Routes>
              {/* Authentication Routes */}
              <Route path="/get-user" element={<UserChecker />} />
              <Route path="/hospital/:slug" element={<FacilityEntry />} />
              <Route path="/hospital" element={<FacilityEntry />} />
              <Route path="/hospitals" element={<Facilities />} />
              <Route path="auth" element={<Auth />} />
              <Route path="/verify-otp" element={<VerifyOTP />} />
              <Route
                path="/forgot-password-otp"
                element={<ForgotPasswordOtp />}
              />
              <Route
                path="/forgot-password-reset"
                element={<ResetPassword />}
              />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route
                path="/login"
                element={<Navigate to="/auth?mode=signin" replace />}
              />
              <Route
                path="/signup"
                element={<Navigate to="/auth?mode=signup" replace />}
              />{" "}
              <Route
                path="/"
                element={
                  localStorage.getItem("appInitialized") === "initializing" &&
                  localStorage.getItem("signupCompleted") !== "true" ? (
                    <Navigate to="/get-user" replace />
                  ) : (
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  )
                }
              >
                <Route index element={<Home />} />{" "}
                <Route path="specializations" element={<Specializations />} />
                <Route
                  path="specializations/:specializationId/:specializationName"
                  element={<SpecializationURLRedirectHandler />}
                />
                <Route
                  path="specializations/:specializationId/:specializationName/:facilityId/:facilityName"
                  element={<SpecializationURLRedirectHandler />}
                />
                <Route path="doctors" element={<Doctors />} />
                <Route path="availableSlot" element={<TimeSlot />} />
                <Route path="about" element={<AboutUs />} />
                <Route path="find-doctor" element={<FindDoctor />} />
                <Route path="faq" element={<FAQ />} />{" "}
                <Route
                  path="book-appointment"
                  element={<AppointmentBooking />}
                />{" "}
                <Route
                  path="generate-qrcode"
                  element={<CombinedQRGenerate />}
                />
                <Route path="qr-redirect" element={<QRRedirectHandler />} />
                <Route
                  path="specialization-redirect"
                  element={<SpecializationURLRedirectHandler />}
                />
                <Route path="qr-doctors" element={<QRCodeDoctors />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="profile" element={<Profile />} />
                <Route path="booking-guide" element={<HowToBook />} />
              </Route>
              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteHandler>

        {!embedded && <ContactButton />}

        <Toaster
          position="top-center"
          gutter={12}
          containerStyle={{ margin: "8px" }}
          toastOptions={toastOption}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
